import i18n from 'i18next';
import { initReactI18next, useTranslation as originalUseTranslation } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { ui_en as uiEn, ui_ko as uiKo } from './ui';
import { schemas_en as schemaEn, schemas_ko as schemaKo } from './form-kit';

import type { TFunction } from 'i18next';

// 기본 네임스페이스 정의
const defaultNamespaces = ['ui', 'schemas'];

// 사용 가능한 모든 네임스페이스 목록 (자동으로 관리됨)
const allNamespaces = [
  ...defaultNamespaces,
  'login',
  'proxy',
  'tenure-check',
  'register',
  'forgot-password',
  'my-page',
  'admin-dashboard',
  // 새 워크스페이스를 여기에 추가할 필요가 없습니다. 아래의 registerNamespace 함수를 사용하세요.
];

// 리소스 객체 초기화
const resources: Record<string, Record<string, Record<string, unknown>>> = {
  en: {
    ui: uiEn,
    schemas: schemaEn,
  },
  ko: {
    ui: uiKo,
    schemas: schemaKo,
  },
};

// 지원하는 언어 목록
const supportedLanguages = ['en', 'ko'];

// 각 언어별로 빈 객체로 모든 네임스페이스 초기화
supportedLanguages.forEach((lang) => {
  allNamespaces.forEach((ns) => {
    if (!resources[lang]) resources[lang] = {};
    if (!resources[lang][ns]) resources[lang][ns] = {};
  });
});

// i18n 초기화
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    ns: allNamespaces, // 모든 네임스페이스를 등록
    interpolation: {
      escapeValue: false,
    },
    partialBundledLanguages: true,
    react: {
      useSuspense: true,
    },
    detection: {
      order: ['navigator', 'htmlTag', 'path', 'subdomain'],
      caches: ['localStorage'],
    },
  });

type ResourcesType = typeof resources;
type LanguageType = keyof ResourcesType;
type NamespaceType = keyof ResourcesType[LanguageType];

export function useTranslation(ns?: NamespaceType | NamespaceType[]) {
  return originalUseTranslation(ns as string | string[]);
}

// 새 네임스페이스 등록 함수
export const registerNamespace = (ns: string) => {
  if (!allNamespaces.includes(ns)) {
    allNamespaces.push(ns);

    // 모든 지원 언어에 네임스페이스 추가
    supportedLanguages.forEach((lang) => {
      if (!resources[lang][ns]) {
        resources[lang][ns] = {};
        i18n.addResourceBundle(lang, ns, {}, true, true);
      }
    });

    // i18n에 네임스페이스 로드
    i18n.loadNamespaces([ns]);
  }
  return ns;
};

// 언어 변경 함수
export const changeLanguage = (lng: LanguageType) => {
  return i18n.changeLanguage(lng);
};

// 네임스페이스 로드 함수 - 자동 등록 기능 추가
export const loadNamespaces = async (namespaces: string[]) => {
  // 아직 등록되지 않은 네임스페이스 자동 등록
  namespaces.forEach((ns) => registerNamespace(ns));
  return i18n.loadNamespaces(namespaces);
};

// 앱별 리소스 추가 함수 - 자동 등록 기능 추가
export const addResources = (newResources: Record<string, Record<string, Record<string, unknown>>>) => {
  Object.entries(newResources).forEach(([lang, namespaces]) => {
    Object.entries(namespaces).forEach(([ns, translations]) => {
      // 네임스페이스 자동 등록
      registerNamespace(ns);
      i18n.addResourceBundle(lang, ns, translations, true, true);
    });
  });
};

// 현재 언어 가져오기
export const getCurrentLanguage = (): LanguageType => {
  return i18n.language as LanguageType;
};

// 모든 등록된 네임스페이스 가져오기
export const getAllNamespaces = (): string[] => {
  return [...allNamespaces];
};

export { i18n as i18nInstance, TFunction };
